/**
 * IntroScreen — the introduction to MokuLab as a surface of the Mokunet
 * network: community access for place-based management, then the whole life
 * of a place (design, build, operate) on one governed model, buildings made
 * as assemblies, the narrowed types as example projects (one container each),
 * and living systems as a commons.
 *
 * Shown once per viewer (localStorage), reopened from the command bar's
 * Welcome button or `?welcome=1`. It floats over the view inside the shell
 * so the command bar stays usable. Copy comes from the DomainRegistry
 * (NETWORK, PLATFORM, the narrowed catalog); nothing is written here.
 */

import { NETWORK, PLATFORM, STATUS_LABEL, listCommunityTypes, listDomains, type CommunityType } from '../lib/DomainRegistry.js';

const SEEN_KEY = 'ws.intro.v1';

export class IntroScreen {
    private root: HTMLElement;
    private onNavigate: (service: string) => void = () => {};

    constructor(host: HTMLElement) {
        this.root = document.createElement('div');
        this.root.className = 'ws-intro-backdrop';
        this.root.hidden = true;
        host.appendChild(this.root);
        this.root.addEventListener('click', (e) => {
            const t = e.target as HTMLElement;
            if (t === this.root || t.closest('[data-intro-close]')) { this.dismiss(); return; }
            const go = t.closest<HTMLElement>('[data-intro-go]');
            if (go) { this.dismiss(); this.onNavigate(go.dataset.introGo!); }
        });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.root.hidden) this.dismiss(); });
    }

    /** True on first visit and whenever the viewer asked to keep it ('keep'); false once dismissed ('seen'). */
    static shouldShow(): boolean {
        try { return localStorage.getItem(SEEN_KEY) !== 'seen'; } catch { return true; }
    }

    private static keptByViewer(): boolean {
        try { return localStorage.getItem(SEEN_KEY) === 'keep'; } catch { return false; }
    }

    setNavigateHandler(fn: (service: string) => void): void { this.onNavigate = fn; }

    show(): void {
        this.render();
        this.root.hidden = false;
        this.root.querySelector<HTMLElement>('[data-intro-go]')?.focus();
    }

    hide(): void { this.root.hidden = true; }

    isOpen(): boolean { return !this.root.hidden; }

    private dismiss(): void {
        const keep = this.root.querySelector<HTMLInputElement>('#ws-intro-keep')?.checked;
        try { localStorage.setItem(SEEN_KEY, keep ? 'keep' : 'seen'); } catch { /* ignore */ }
        this.hide();
    }

    private render(): void {
        const domains = listDomains();
        const keep = IntroScreen.keptByViewer() ? 'checked' : '';
        this.root.innerHTML = `
            <div class="ws-intro" role="dialog" aria-labelledby="ws-intro-title">
                <div class="ws-intro-head">
                    <div class="ws-intro-eyebrow">${esc(NETWORK.eyebrow)}</div>
                    <button class="ws-icon-btn" data-intro-close title="Close (Esc)"><i class="fa-solid fa-xmark"></i></button>
                </div>
                <div class="ws-intro-body">
                    <h1 id="ws-intro-title">${esc(NETWORK.headline)}</h1>
                    <p class="ws-intro-lead">${esc(NETWORK.lead)}</p>

                    <div class="ws-intro-points">
                        ${NETWORK.points.map(p => `
                            <div class="ws-intro-point">
                                <div class="ws-intro-point-title"><i class="fa-solid ${p.icon}"></i>${esc(p.title)}</div>
                                <p>${esc(p.blurb)}</p>
                            </div>`).join('')}
                    </div>

                    <div class="ws-section-title">${esc(NETWORK.examples.title)}</div>
                    <p class="ws-intro-examples-lead">${esc(NETWORK.examples.lead)}</p>
                    <div class="ws-intro-catalog">
                        ${domains.map(d => `
                            <div class="ws-intro-catalog-col" style="--swatch:${d.accent}">
                                <div class="ws-intro-catalog-head"><span class="ws-swatch"></span>${esc(d.title)}</div>
                                <div class="ws-intro-catalog-note">${esc(NETWORK.examples.columnNote[d.id])}</div>
                                ${listCommunityTypes(d.id).map(t => this.renderType(t)).join('')}
                            </div>`).join('')}
                    </div>
                    <div class="ws-intro-stage"><span class="ws-pill" data-status="planned">${esc(NETWORK.stage.label)}</span> ${NETWORK.stage.items.map(esc).join(' ')} ${esc(PLATFORM.trustLine)}</div>

                    <div class="ws-intro-links">
                        ${NETWORK.links.map(l => `<a href="${l.href}" target="_blank" rel="noopener" title="${esc(l.blurb)}">${esc(l.title)} <i class="fa-solid fa-arrow-up-right-from-square"></i></a>`).join('')}
                    </div>
                </div>
                <div class="ws-intro-foot">
                    <label class="ws-intro-keep"><input type="checkbox" id="ws-intro-keep" ${keep}> Show at start</label>
                    <span class="ws-intro-proverb" title="${esc(NETWORK.proverb.gloss)}">${esc(NETWORK.proverb.text)}</span>
                    <span class="ws-spacer"></span>
                    ${domains.map(d => `<button class="ws-link-btn" data-intro-go="${d.id}" type="button"><i class="fa-solid ${d.icon}"></i> ${esc(d.navTitle)}</button>`).join('')}
                    <button class="ws-link-btn" data-intro-go="overview" type="button"><i class="fa-solid fa-flask"></i> Enter the lab</button>
                    <button class="ws-link-btn primary" data-intro-go="start" type="button"><i class="fa-solid fa-circle-play"></i> Start a project</button>
                </div>
            </div>`;
    }

    /** One catalog row: title, place and statute, status. The goals a type serves stay in
     *  the data and land on the model as project metadata (Pset_MokuLab_CommunityType);
     *  they are not shown here. */
    private renderType(t: CommunityType): string {
        const detail = [t.placeTitle, t.statute.join(', ')].filter(Boolean).join(' · ');
        return `
            <div class="ws-intro-type" title="${esc(t.examples.join(', '))}${t.statuteNote ? ` — ${esc(t.statuteNote)}` : ''}">
                <span class="ws-intro-type-title">${esc(t.title)}</span>
                <span class="ws-intro-type-meta">${esc(detail)}</span>
                <span class="ws-pill" data-status="${t.status}">${STATUS_LABEL[t.status]}</span>
            </div>`;
    }
}

function esc(s: string): string {
    return s.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
}
